import React from "react"
import { TaskColumnName, TreeGridTask } from "./types"
import { Option } from "../types"
import { TimeZoneType } from "../../constants/timezones"
import { useUrlWithContext } from "../../hooks/use-url-with-context"
import { useRouter } from "../../hooks"
import { useTreeGridFilters } from "../hooks/use-treegrid-filters"
import { makeTreegridLayout } from "./layout"
import { makeTreeGridTaskRows } from "./rows"
import { DateTimeService } from "../../services/date-time-service"
import {
  createTreegrid,
  destroyTreegrid,
  getSummaryTextWithRowCount,
  syncDataFromServerToGrid,
  updateGridData,
} from "../utils/tree-grid"
import {
  getColorFromColorHtml,
  getShouldRemoveTaskRowBasedOnContext,
  getStateFromStateIcon,
  mapGroupByToColumns,
  mapTranslationsToColor,
  mapTranslationsToGroupBy,
} from "../utils"
import { useTranslations } from "../hooks/use-translations"
import { TasksViewOptions } from "../../tasks/components/TasksViewSelect"
import { useAppContext } from "../../app-context/use-app-context"
import { useContextOptions } from "../../context-options/hooks/use-context-options"
import { TaskViewModel } from "../../tasks/api/task"
import { useTaskMutations } from "../../tasks/hooks/use-task-mutations"
import { useUpdateTaskStatus } from "../../tasks/hooks/use-update-task-status"
import { getMaxDate, getMinDate } from "../../utils"
import { GroupBy } from "../../types/common"
import { HolidayViewModel } from "../../holidays/api/holiday"
import { SystemStatus } from "../../lib/system-status"
import { makeOnSearchChange } from "../base/event-handlers/on-search-change"
import { makeOnFilterFinish } from "../base/event-handlers/on-filter-finish"
import { getGanttVisibleRange } from "../utils/gantt-utils"
import { makeOnAfterSectionResize } from "../base/event-handlers/on-after-section-resize"
import { CalendarSettingsViewModel } from "../../calendar-settings/api/calendar-settings"
import { makeCalendarSettingsId } from "../../calendar-settings/api/calendar-settings-id"
import { useCanEditDateByCalendarSettingsId } from "../hooks/use-can-edit-date-by-calendar-settings-id"
import { useTranslation } from "react-i18next"
import { useSnackbar } from "notistack"
import { useFileDownload } from "../../storage/hooks/use-file-download"

const id = "__treegrid_task_list__"

export const useTaskList = (props: TaskListProps) => {
  const translations = useTranslations()
  const projectTranslations = useTranslation("project").t
  const { enqueueSnackbar } = useSnackbar()
  const [navigateToTaskId, setNavigateToTaskId] = React.useState<string | null>(null)
  const { createPathWithContext } = useUrlWithContext()
  const { appContext } = useAppContext()
  const { userMembershipContextOptions } = useContextOptions()
  const { history, ...router } = useRouter()
  const { showFilters, toggleFilters } = useTreeGridFilters({ gridId: id })
  const { initTaskUpdate } = useTaskMutations()
  const { updateTaskStatus, updatedTasks, resetUpdatedTasksAfterSync } = useUpdateTaskStatus()
  const zoomRef = React.useRef<string | null>(null)
  const query = router.query as { view?: TasksViewOptions }
  const tasksView = query.view || "currentAndFuture"
  const { openFileInBrowser } = useFileDownload()
  translations.toolbarTaskListSummaryText = getSummaryTextWithRowCount(
    translations.toolbarTaskListSummaryText,
    props.tasks.length
  )
  React.useEffect(() => {
    if (navigateToTaskId) {
      const path = createPathWithContext(`/tasks/${navigateToTaskId}/basic`)
      history.push(path)
    }
  }, [createPathWithContext, history, navigateToTaskId])

  // Adding keydown listener to handle ctrl/cmd + c and ctrl/cmd + v for copy paste functionality,
  // and stopping the event propagation so that it doesn't trigger any other copy paste handlers
  // in the app when the treegrid is focused.
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && (e.key === "c" || e.key === "v")) {
        const grid = window.Grids[id]
        if (grid) {
          e.stopImmediatePropagation()
        }
      }
    }
    document.addEventListener("keydown", handleKeyDown, true) // true = capture phase
    return () => document.removeEventListener("keydown", handleKeyDown, true)
  }, [])

  const showGantt = tasksView === "currentAndFuture" || tasksView === "all"
  const showGroupBy = appContext.mainContext?.type === "org"
  const allPlannedStartDates = props.tasks.map((task) => task.plannedStartDate).filter(Boolean)
  const allPlannedEndDates = props.tasks.map((task) => task.plannedEndDate).filter(Boolean)
  const allActualStartDates = props.tasks.map((task) => task.actualStartDate).filter(Boolean)
  const allActualEndDates = props.tasks.map((task) => task.actualEndDate).filter(Boolean)
  const allDates = allPlannedStartDates.concat(allPlannedEndDates).concat(allActualStartDates).concat(allActualEndDates)
  const minStartDate = getMinDate(allDates)
  const maxEndDate = getMaxDate(allDates)
  const defaultGanttZoomRange = getGanttVisibleRange({
    startDate: new Date(),
    endDate: maxEndDate,
  })

  const layout = makeTreegridLayout({
    id,
    showFilters,
    translations,
    showGantt,
    showGanttToolbarActions: showGantt,
    groupBy: GroupBy.NO_GROUPING,
    tasksView,
    showGroupBy,
    ganttZoomLeftEdgeRefDate: minStartDate || new Date(),
    ganttZoomRightEdgeRefDate: maxEndDate || new Date(),
    ...props,
  })

  const { dateFormat, timeZone, options } = props
  const canEditDateByCalendarSettingsId = useCanEditDateByCalendarSettingsId(props.calendarSettingsById)
  const dateTimeService = new DateTimeService({
    dateFormat: props.dateFormat,
    timeZone: props.timeZone,
  })

  // @ts-ignore
  window.Grids["OnFilterToggle"] = function () {
    toggleFilters()
  }

  // Disable editing of weekends and holidays based on the parent project's calendar settings
  window.Grids.OnCanEditDate = function (grid, row: TRow & TreeGridTask, col, date) {
    const projectCalendarSettingsId = makeCalendarSettingsId({ type: "project", id: row.projectId })
    const canEditDate = canEditDateByCalendarSettingsId[projectCalendarSettingsId]
    if (canEditDate) return canEditDate(date)

    return dateTimeService.isAllowedDays(date, {
      disabledDays: props.weekendDays,
      disabledDates: props.holidays.map((h) => h.date).filter((d): d is Date => !!d),
    })
  }

  window.Grids.OnValueChanged = function (
    grid: TGrid,
    row: TRow & TreeGridTask,
    column: TaskColumnName,
    newValue,
    oldValue
  ) {
    // NOTE: Returning early from this function will cause unexpected behavior in the treegrid
    const isColumnEditAllowed = (row as any)[`${column}CanEdit`] === 1
    const hasValueChanged = newValue !== oldValue
    const shouldDispatchUpdateRequest = isColumnEditAllowed && hasValueChanged
    if (shouldDispatchUpdateRequest) {
      let value = adaptValue({ column, newValue, oldValue })

      // If the column is a date column, then we need to adjust the timezone
      if (isDateColumn(column) && value) {
        const dateTimeService = new DateTimeService({
          dateFormat: props.dateFormat,
          timeZone: props.timeZone,
          enableTimeComponent: row.enableTimeComponent,
        })
        const date = new Date(value)
        const adjustedDate = dateTimeService.addTimezoneOffset(date, "UTC")
        value = adjustedDate.toISOString()
      }

      if (column === "ganttBarColor") {
        row.GanttGanttClass = value === "Default" ? "Blue" : value // changes the color of the gantt bar immediately
        value = mapTranslationsToColor(translations)[value]
      }

      if (column === "status") {
        const [mainStatusId, subStatusId] = newValue.toString().split("_")
        const status = SystemStatus.statusByIndex[parseInt(mainStatusId)]
        updateTaskStatus(row.id, { status, subStatusId: subStatusId ?? null })
        return
      }

      if (column === "actualStartDate" || column === "actualEndDate") {
        const startRaw = column === "actualStartDate" ? value : row.actualStartDate
        const endRaw = column === "actualEndDate" ? value : row.actualEndDate
        let sendReq = true
        // actual start and end dates cannot be in the future
        if (startRaw || endRaw) {
          // The column being edited comes from TreeGrid in local timezone, needs local conversion
          // The other column comes from server already as UTC ISO, use as-is
          const startISO = startRaw
            ? column === "actualStartDate"
              ? DateTimeService.toLocalISOStringFormat(new Date(startRaw))
              : new Date(startRaw).toISOString()
            : ""
          const endISO = endRaw
            ? column === "actualEndDate"
              ? DateTimeService.toLocalISOStringFormat(new Date(endRaw))
              : new Date(endRaw).toISOString()
            : ""
          const nowISO = row.enableTimeComponent
            ? DateTimeService.toLocalISOStringFormat(new Date())
            : new Date().toISOString()

          // actual start and end dates cannot be in the future
          if (column === "actualStartDate" && startISO && startISO > nowISO) {
            enqueueSnackbar(projectTranslations("projectDetails.actualStartDateCannotBeInFuture"), {
              variant: "error",
            })
            value = oldValue === "" ? "" : new Date(oldValue).toISOString()
            sendReq = false
          } else if (column === "actualEndDate" && endISO && endISO > nowISO) {
            enqueueSnackbar(projectTranslations("projectDetails.actualEndDateCannotBeInFuture"), { variant: "error" })
            value = oldValue === "" ? "" : new Date(oldValue).toISOString()
            sendReq = false
          }
          // actual start date cannot be after actual end date and vice versa
          else if (startISO && endISO) {
            const startCompare = row.enableTimeComponent ? startISO : startISO.substring(0, 10)
            const endCompare = row.enableTimeComponent ? endISO : endISO.substring(0, 10)

            if (column === "actualEndDate" && endCompare < startCompare) {
              enqueueSnackbar(projectTranslations("projectDetails.actualEndCannotBeBeforeStartError"), {
                variant: "error",
              })
              value = oldValue === "" ? "" : new Date(oldValue).toISOString()
              sendReq = false
            }
            if (column === "actualStartDate" && startCompare > endCompare) {
              enqueueSnackbar(projectTranslations("projectDetails.actualStartCannotBeAfterEndError"), {
                variant: "error",
              })
              value = oldValue === "" ? "" : new Date(oldValue).toISOString()
              sendReq = false
            }
          }
        }
        // If the value is valid and has passed all the validations,
        // then we can send the update request to the server. If not,
        // we should revert back the value in the grid and not send the request.
        if (sendReq) {
          updateTaskStatus(row.id, { [column]: value })
          return
        }
        return value ? new Date(value) : ""
      }

      // Special handling for multi-select columns to change the value structure
      if (
        column === "managers" ||
        column === "customers" ||
        column === "participants" ||
        column === "suppliers" ||
        column === "workspaces"
      ) {
        const oldIds: string[] = oldValue.split(";").filter(Boolean)
        const newIds: string[] = newValue.split(";").filter(Boolean)

        const add = newIds
          .filter((id) => !oldIds.includes(id))
          .map((id) => {
            const option = options.find((opt) => opt.id === id)
            return { id, name: option?.name ?? "" }
          })

        const remove = oldIds
          .filter((id) => !newIds.includes(id))
          .map((id) => {
            const option = options.find((opt) => opt.id === id)
            return { id, name: option?.name ?? "" }
          })

        value = { add, remove }
      }
      initTaskUpdate(row.id, { field: column as any, value }).then((responseData) => {
        if (!responseData || !grid) return
        const tasks = "tasks" in responseData ? responseData.tasks : [responseData]
        const tasksToUpdateInGrid: TaskViewModel[] = []
        const tasksToRemoveFromGrid: TaskViewModel[] = []
        tasks.forEach((task) => {
          const shouldRemoveRow = getShouldRemoveTaskRowBasedOnContext(task, appContext, userMembershipContextOptions)
          if (shouldRemoveRow) {
            tasksToRemoveFromGrid.push(task)
          } else {
            tasksToUpdateInGrid.push(task)
          }
        })
        // PRESERVE LINKS AND FILES FROM ORIGINAL TASKS
        const enrichedTasks = getEnrichedTasks(tasksToUpdateInGrid, props.tasks)
        const updatedTaskRows = makeTreeGridTaskRows({
          tasks: enrichedTasks,
          dateFormat,
          timeZone,
          options,
          translations,
        })
        syncDataFromServerToGrid(grid, updatedTaskRows)

        tasksToRemoveFromGrid.forEach((task) => {
          const row = grid.GetRowById(task.id)
          if (row) grid.AnimateRow(row, "Delete", undefined, () => grid.RemoveRow(row))
        })
      })
    }
    return newValue
  }

  // @ts-ignore
  window.Grids.OnClick = function (grid, row, col) {
    // Disable header click for open column
    if (col === "open" && row.Kind === "Header") {
      return true
    }
    if (col === "open") {
      const isTaskRow = row.Def.Name === "R"
      if (isTaskRow) setNavigateToTaskId(row.id)
    }
    if (col === "ZoomFit" && grid) {
      const ganttZoomRange = getGanttVisibleRange({
        startDate: minStartDate,
        endDate: maxEndDate,
      })
      grid.ZoomTo(ganttZoomRange.start, ganttZoomRange.end)
      return true
    }
    if (col === "linksAndAttachments") {
      const event = window.event
      const target = event?.target as HTMLElement
      const fileLink = target?.closest("[data-file-id]")
      if (fileLink) {
        const fileId = fileLink.getAttribute("data-file-id")
        if (fileId) {
          openFileInBrowser(fileId)
          return true
        }
      }
    }
  }

  // @ts-ignore
  window.Grids.OnSearchChange = makeOnSearchChange({ translations })
  window.Grids.OnFilterFinish = makeOnFilterFinish({ translations, gridId: "tasks" })
  window.Grids.OnAfterSectionResize = makeOnAfterSectionResize({ ganttZoomRange: defaultGanttZoomRange })

  // @ts-ignore
  window.Grids.onClickTitleLink = function (link) {
    const linkWithContext = createPathWithContext(link)
    history.push(linkWithContext)
  }

  window.Grids.OnGetSortValue = function (grid: TGrid, row: TRow & TreeGridTask, column: TaskColumnName, value: any) {
    if (column === "plannedEndDate" || column === "plannedStartDate") {
      const shouldSortToBottom = !value
      if (shouldSortToBottom) return Number.MAX_SAFE_INTEGER
    }
    return value
  }

  window.Grids.OnRenderFinish = function (grid) {
    if (grid) {
      grid.ZoomTo(defaultGanttZoomRange.start, defaultGanttZoomRange.end)
    }
  }

  window.Grids.OnZoom = function (_, zoom) {
    zoomRef.current = zoom
  }

  // @ts-ignore
  window.Grids.OnSelectGroupByChange = function (grid: TGrid, groupBy: string) {
    if (grid) {
      const groubByValue = mapTranslationsToGroupBy(translations)[groupBy] as GroupBy
      const Group = mapGroupByToColumns(groubByValue)
      grid.DoGrouping(Group)
    }
  }

  React.useEffect(() => {
    const grid = window.Grids[id]
    if (grid) {
      const tasksToUpdateInGrid: TaskViewModel[] = []
      const enrichedTasks = getEnrichedTasks(updatedTasks, props.tasks)
      enrichedTasks.forEach((task) => {
        const shouldRemoveRow = getShouldRemoveRow(task, tasksView)
        if (shouldRemoveRow) {
          const row = grid.GetRowById(task.id)
          if (row) grid.AnimateRow(row, "Delete", undefined, () => grid.RemoveRow(row))
        } else {
          tasksToUpdateInGrid.push(task)
        }
      })
      if (tasksToUpdateInGrid.length) {
        const taskRows = makeTreeGridTaskRows({
          tasks: tasksToUpdateInGrid,
          dateFormat,
          timeZone,
          options,
          translations,
        })
        syncDataFromServerToGrid(grid, taskRows)
        resetUpdatedTasksAfterSync()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatedTasks])

  return { layout, tasksView }
}
const getEnrichedTasks = (updatedTasks: TaskViewModel[], allTasks: TaskViewModel[]) => {
  // Enrich updated tasks with links and files from original data
  // The API response may not include these, so we preserve them from the source
  return updatedTasks.map((updatedTask) => {
    const originalTask = allTasks.find((t) => t.id === updatedTask.id)
    if (originalTask) {
      return {
        ...updatedTask,
        links: originalTask.links || [],
        files: originalTask.files || [],
        canViewTaskAttachments: originalTask.canViewTaskAttachments,
      }
    }
    return updatedTask
  })
}
const TaskList = (props: TaskListProps) => {
  const translations = useTranslations()
  const { layout, tasksView } = useTaskList(props)
  const { tasks, dateFormat, timeZone, options } = props

  React.useEffect(() => {
    const data = makeTreeGridTaskRows({ tasks, dateFormat, timeZone, options, translations })
    createTreegrid({ id, layout, data })
    return () => destroyTreegrid(id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.defaultVisibleColumns, props.defaultColumnOrder])

  React.useEffect(() => {
    const data = makeTreeGridTaskRows({ tasks, dateFormat, timeZone, options, translations })
    updateGridData({ id, data })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tasksView])

  return <div id={id} style={{ height: "calc(100vh - 128px)" }} data-test="task-list-container"></div>
}

export default TaskList

function adaptValue({ column, newValue, oldValue }: { column: string; newValue: string; oldValue: string }) {
  let adaptedValue: any = newValue
  if (isDateColumn(column)) {
    if (newValue) adaptedValue = new Date(newValue).toISOString()
    else adaptedValue = ""
  }
  if (isSelectionColumn(column)) {
    const oldArray: string[] = oldValue.split(";").filter(Boolean)
    const newArray: string[] = newValue.split(";").filter(Boolean)
    const add = newArray.filter((id) => !oldArray.includes(id))
    const remove = oldArray.filter((id) => !newArray.includes(id))
    adaptedValue = { add, remove }
  }
  if (column === "state") {
    adaptedValue = getStateFromStateIcon(newValue)
  }
  if (column === "ganttBarColor") {
    adaptedValue = getColorFromColorHtml(newValue)
  }
  return adaptedValue
}

function isDateColumn(columnName: string) {
  return (
    columnName === "actualEndDate" ||
    columnName === "actualStartDate" ||
    columnName === "plannedEndDate" ||
    columnName === "plannedStartDate"
  )
}

function isSelectionColumn(columnName: string) {
  return (
    columnName === "participants" ||
    columnName === "managers" ||
    columnName === "suppliers" ||
    columnName === "workspaces"
  )
}

function getShouldRemoveRow(task: TaskViewModel, tasksView: TasksViewOptions) {
  if (tasksView === "all") return false
  if (task.archived) return tasksView !== "archived"
  if (task.isRejected) return tasksView !== "rejected"
  if (task.isCompleted) return tasksView !== "completed"
  if (task.isNotStarted || task.isInProgress) return tasksView !== "currentAndFuture"
  return false
}

type TaskListProps = {
  defaultColumnOrder: TaskColumnName[]
  defaultVisibleColumns: TaskColumnName[]
  tasks: TaskViewModel[]
  options: Option[]
  gridInfo: string
  dateFormat: string
  dateSeparator: string
  firstDayOfWeek: DayOfWeek
  timeZone: TimeZoneType
  weekendDays: DayOfWeek[]
  holidays: HolidayViewModel[]
  calendarSettingsById: Record<string, CalendarSettingsViewModel>
}
